import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { DataFetch, DataPage, DataQuery } from '../../models/data-source';
import { ConfirmationDialogService } from '../../shared/ui/confirmation-dialog/confirmation-dialog.service';
import { DataFilterField, DataFilterValues } from '../../shared/ui/data-filter/data-filter.component';
import { DataTableColumn } from '../../shared/ui/data-table/data-table.component';
import { TypedConfirmationDialogComponent } from '../../shared/ui/typed-confirmation-dialog/typed-confirmation-dialog.component';

type TokenKind = 'font' | 'space' | 'shape' | 'color' | 'value';
interface TokenFamily { id: string; title: string; summary: string; kind: TokenKind; prefixes: string[]; tokens: { name: string; value: string }[] }
interface GalleryRow { id: number; name: string; status: string; city: string; value: number }

const STATUSES = ['Active', 'Pending', 'Closed'];
const CITIES = ['Austin', 'Dallas', 'Houston', 'El Paso'];
const ROWS: GalleryRow[] = Array.from({ length: 60 }, (_, i) => ({
  id: i + 1,
  name: `Sample account ${i + 1}`,
  status: STATUSES[i % STATUSES.length],
  city: CITIES[i % CITIES.length],
  value: (i * 137) % 5000,
}));

/** Development-only showcase of every shared control. Remove the route before shipping. */
@Component({
  selector: 'app-ui-gallery',
  templateUrl: './ui-gallery.component.html',
  styleUrls: ['./ui-gallery.component.css'],
})
export class UiGalleryComponent {
  /** Grouped by what each layer is; the subtitle is where it lives in the source tree. */
  readonly groups = [
    // Filled from the token families that the loaded theme actually declares.
    { title: 'Theme', path: 'styles/theme.css', sections: [] as { id: string; label: string }[] },
    { title: 'Base styles', path: 'styles/foundation.css', sections: [
      { id: 'buttons', label: 'Buttons' },
      { id: 'badges', label: 'Badges & alerts' },
      { id: 'cards', label: 'Cards' },
      { id: 'forms', label: 'Form fields' },
    ] },
    { title: 'Shared controls', path: 'shared/ui/', sections: [
      { id: 'confirmation-dialog', label: 'Confirmation dialog' },
      { id: 'data-filter', label: 'Data filter' },
      { id: 'data-list', label: 'Data list' },
      { id: 'data-table', label: 'Data table' },
      { id: 'date-field', label: 'Date field' },
      { id: 'icon', label: 'Icons' },
      { id: 'menu', label: 'Action menus' },
      { id: 'popover', label: 'Form popovers' },
      { id: 'signature-pad', label: 'Signature pad' },
      { id: 'time-field', label: 'Time field' },
      { id: 'typed-confirmation-dialog', label: 'Typed confirmation dialog' },
    ] },
    { title: 'Angular Material', path: '@angular/material', sections: [
      { id: 'material', label: 'Not migrated yet' },
    ] },
  ];

  /**
   * Token families. Names and values are read from the loaded stylesheet, so this
   * page never keeps its own copy of the theme; only the family descriptions live here.
   */
  readonly tokenFamilies: TokenFamily[] = [
    { id: 'theme-typography', title: 'Typography', summary: 'Font family, the type scale (md is body text) and line heights.', kind: 'font', prefixes: ['font-', 'line-height-'], tokens: [] },
    { id: 'theme-spacing', title: 'Spacing', summary: 'The 4px rhythm used by every margin, padding and gap utility.', kind: 'space', prefixes: ['space-'], tokens: [] },
    { id: 'theme-radius-shadow', title: 'Radius & shadow', summary: 'Corner radii and the two elevation levels (raised and dialog).', kind: 'shape', prefixes: ['radius-', 'shadow-'], tokens: [] },
    { id: 'theme-colour', title: 'Colour', summary: 'The product palette: primary action, status colours, text, borders and surfaces.', kind: 'color', prefixes: ['color-'], tokens: [] },
    { id: 'theme-interaction', title: 'Interaction', summary: 'Hover and selected tints and the transition speed shared by quiet controls.', kind: 'value', prefixes: ['interaction-'], tokens: [] },
    { id: 'theme-sizing', title: 'Sizing', summary: 'Control height, table row and header heights, and the record column width.', kind: 'value', prefixes: ['control-', 'table-row-', 'table-header-height', 'record-'], tokens: [] },
    { id: 'theme-table-surface', title: 'Table surface', summary: 'Dense-table greys and the bulk-selection band.', kind: 'color', prefixes: ['table-', 'bulk-'], tokens: [] },
    { id: 'theme-workflow-canvas', title: 'Workflow canvas', summary: 'Ground, grid and panels of the workflow drawing surface.', kind: 'color', prefixes: ['canvas-'], tokens: [] },
    { id: 'theme-severity', title: 'Severity', summary: 'Error, warning and OK tints for validation lists and status lamps.', kind: 'color', prefixes: ['severity-'], tokens: [] },
    { id: 'theme-other', title: 'Other', summary: 'Tokens outside the families above.', kind: 'value', prefixes: [''], tokens: [] },
  ];

  readonly buttonVariants = ['primary', 'secondary', 'danger', 'outline-primary', 'outline-secondary',
    'outline-danger', 'light', 'ghost', 'link'];
  readonly fontIcons = ['settings', 'search', 'add', 'edit', 'delete', 'more_vert', 'map', 'folder', 'check', 'close'];
  readonly svgIcons = ['br:compose', 'br:window', 'br:maximize', 'br:dock-right', 'br:folder', 'br:folder-open'];
  readonly colors = ['#0d6efd', '#198754', '#dc3545', '#fd7e14'];

  currentView = 'Table';
  readonly views = ['Table', 'Board', 'Map'];

  text = '';
  select = 'b';
  checked = true;
  date: string | null = null;
  dateTime: string | null = null;
  time: string | null = null;
  signature: string | null = null;

  popoverName = '';
  popoverStatus = 'Active';

  filterValues: DataFilterValues = {};
  readonly filterFields: readonly DataFilterField[] = [
    { id: 'status', label: 'Status', allLabel: 'All statuses', options: STATUSES.map(s => ({ value: s, label: s })) },
    { id: 'city', label: 'City', allLabel: 'All cities', options: CITIES.map(c => ({ value: c, label: c })), pinned: false },
    { id: 'value', label: 'Value', options: [], kind: 'number', pinned: false },
  ];

  readonly columns: readonly DataTableColumn[] = [
    { id: 'id', label: 'ID', align: 'end' },
    { id: 'name', label: 'Name' },
    { id: 'status', label: 'Status' },
    { id: 'city', label: 'City' },
    { id: 'value', label: 'Value', align: 'end' },
  ];

  /** In-memory source: search, choice filters, sort and paging, no backend. */
  readonly fetch: DataFetch<GalleryRow> = (q: DataQuery): Observable<DataPage<GalleryRow>> => {
    const term = q.search.trim().toLowerCase();
    let rows = ROWS.filter(r => !term || r.name.toLowerCase().includes(term) || r.city.toLowerCase().includes(term));
    for (const [key, value] of Object.entries(q.filters)) {
      if (value === null || value === '' || typeof value === 'object') continue;
      rows = rows.filter(r => String(r[key as keyof GalleryRow]) === String(value));
    }
    if (q.sort) {
      const key = q.sort as keyof GalleryRow;
      rows = [...rows].sort((a, b) => (a[key] < b[key] ? -1 : a[key] > b[key] ? 1 : 0) * (q.desc ? -1 : 1));
    }
    const start = q.page * q.pageSize;
    return of({ items: rows.slice(start, start + q.pageSize), total: rows.length });
  };

  constructor(private readonly confirmation: ConfirmationDialogService, private readonly dialog: MatDialog) {
    this.readThemeTokens();
  }

  /** Collects every --br-* variable declared on :root, in declaration order. */
  private readThemeTokens(): void {
    const root = getComputedStyle(document.documentElement);
    const seen = new Set<string>();
    for (const sheet of Array.from(document.styleSheets)) {
      let rules: CSSRuleList;
      try { rules = sheet.cssRules; } catch { continue; } // cross-origin sheet
      for (const rule of Array.from(rules)) {
        if (!(rule instanceof CSSStyleRule) || rule.selectorText !== ':root') continue;
        for (const prop of Array.from(rule.style)) {
          if (!prop.startsWith('--br-') || seen.has(prop)) continue;
          seen.add(prop);
          const key = prop.slice('--br-'.length);
          const family = this.tokenFamilies.find(f => f.prefixes.some(prefix => key.startsWith(prefix)))!;
          family.tokens.push({ name: prop, value: root.getPropertyValue(prop).trim() });
        }
      }
    }
    this.groups[0].sections = this.tokenFamilies.filter(f => f.tokens.length).map(f => ({ id: f.id, label: f.title }));
  }

  scrollTo(id: string): void {
    document.getElementById(`gallery-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  confirm(danger: boolean): void {
    this.confirmation.confirm({
      title: danger ? 'Delete account?' : 'Archive account?',
      message: 'This is a sample confirmation. Nothing is changed.',
      confirmLabel: danger ? 'Delete' : 'Archive',
      danger,
    });
  }

  confirmTyped(): void {
    this.dialog.open(TypedConfirmationDialogComponent, {
      data: { title: 'Delete all data?', message: 'Type the word below to continue. Nothing is changed.',
        confirmationText: 'DELETE', confirmLabel: 'Delete everything' },
    });
  }
}
